import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Plus, Trash2, Edit, MoreVertical, FolderPlus } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { formatCurrency } from "../../lib/utils";

export function CategorySection({
  categories, dishes, dailyItems, menu,
  getDishesForCategory, openDishPicker,
  openEditDish, handleDeleteDish, handleRemoveDailyItem,
  onCreateCategory,
}) {
  if (categories.length === 0) {
    return (
      <Card className="border-dashed border-2">
        <CardContent className="py-16 text-center">
          <FolderPlus className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-heading text-lg font-semibold mb-2">Sin categorias</h3>
          <p className="text-muted-foreground mb-6">Crea categorias para organizar los platos de tu menu</p>
          <Button onClick={onCreateCategory}><Plus className="w-4 h-4 mr-2" /> Crear categoria</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {categories.map((category) => (
        <Card key={category.category_id} className="border-none shadow-soft" data-testid={`category-${category.category_id}`}>
          <CardHeader className="pb-4">
            <CardTitle className="font-heading flex items-center gap-2">
              {category.name}
              {menu?.menu_type === "daily" ? (
                <Badge variant="secondary" className="ml-2">
                  {dailyItems.filter(i => i.category_id === category.category_id).length} vinculados
                </Badge>
              ) : (
                <Badge variant="secondary" className="ml-2">
                  {getDishesForCategory(category.category_id).length} platos
                </Badge>
              )}
              {menu?.menu_type === "daily" && (
                <Button size="sm" variant="outline" className="ml-auto"
                  onClick={() => openDishPicker(category.category_id, category.name)}
                  data-testid={`link-dishes-${category.category_id}`}>
                  <Plus className="w-4 h-4 mr-1" /> Vincular platos
                </Button>
              )}
            </CardTitle>
            {category.description && <p className="text-sm text-muted-foreground">{category.description}</p>}
          </CardHeader>
          <CardContent>
            {menu?.menu_type === "daily" ? (
              <DailyItemGrid items={dailyItems.filter(i => i.category_id === category.category_id)} onRemove={handleRemoveDailyItem} />
            ) : (
              <RegularDishGrid dishes={getDishesForCategory(category.category_id)} onEdit={openEditDish} onDelete={handleDeleteDish} />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DailyItemGrid({ items, onRemove }) {
  if (items.length === 0) {
    return <p className="text-muted-foreground text-sm text-center py-8">Vincula platos de tus otros menus</p>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => (
        <div key={item.dish_id} className="p-4 rounded-xl border bg-card" data-testid={`daily-item-${item.dish_id}`}>
          {item.image_url && (
            <div className="w-full h-28 rounded-lg overflow-hidden mb-2">
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-sm">{item.name}</h4>
              {item.description && <p className="text-xs text-muted-foreground line-clamp-1">{typeof item.description === "string" ? item.description : ""}</p>}
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => onRemove(item.dish_id)} data-testid={`unlink-${item.dish_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">Ref: {formatCurrency(item.price)}</span>
            {item.cost > 0 && <span className="text-xs text-muted-foreground">Coste: {formatCurrency(item.cost)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function RegularDishGrid({ dishes, onEdit, onDelete }) {
  if (dishes.length === 0) {
    return <p className="text-muted-foreground text-sm text-center py-8">No hay platos en esta categoria</p>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {dishes.map((dish) => (
        <div key={dish.dish_id} className={`rounded-xl border overflow-hidden ${dish.is_active ? 'bg-card' : 'bg-muted/50 opacity-60'}`}
          data-testid={`dish-${dish.dish_id}`}>
          {dish.image_url && (
            <div className="w-full h-32 overflow-hidden bg-muted">
              <img src={dish.image_url} alt={dish.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          )}
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-semibold">{dish.name}</h4>
                {dish.description && <p className="text-sm text-muted-foreground line-clamp-2">{typeof dish.description === "string" ? dish.description : ""}</p>}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(dish)}><Edit className="w-4 h-4 mr-2" /> Editar</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDelete(dish.dish_id)} className="text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="font-mono text-lg font-semibold text-primary">{formatCurrency(dish.price)}</span>
              {dish.cost > 0 && <Badge variant="outline" className="text-xs">Margen: {dish.margin?.toFixed(0)}%</Badge>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
